import { Box, Tooltip } from '@mui/material';
import InfoIcon from '@mui/icons-material/InfoOutlined';
import { ASSIGNMENTS_STATUS, ASSIGNMENTS_STATUS_OPTIONS, PENDING_ASSIGNMENT_TOOLTIP } from '../constants';
import { FieldProps, TextField, useFieldHelper } from 'framework/ui/field';
import { useDateService } from 'framework/utils';

const IS_VIRTUAL_SOURCE = 'isVirtual';
const FROM_SOURCE = 'from';
const TO_SOURCE = 'to';
const BACKEND_PENDING_STATUS = 'PENDING';
const BACKEND_APPROVED_STATUS = 'APPROVED';

const AssignmentStatusField = ({ source = 'status', label, ...props }: FieldProps) => {
  const { getValue, getLabel } = useFieldHelper({ source, label });
  const { getValue: getIsVirtual } = useFieldHelper({ source: IS_VIRTUAL_SOURCE });
  const { getValue: getFrom } = useFieldHelper({ source: FROM_SOURCE });
  const { getValue: getTo } = useFieldHelper({ source: TO_SOURCE });
  const { getToday, getMomentDateObj } = useDateService();

  const resolveStatus = (): string => {
    if (getIsVirtual()) {
      return ASSIGNMENTS_STATUS.VIRTUAL;
    }
    if (getValue() === BACKEND_PENDING_STATUS) {
      return ASSIGNMENTS_STATUS.PENDING;
    }

    if (getValue() === BACKEND_APPROVED_STATUS) {
      const today = getToday();
      const fromRaw = getFrom();
      const toRaw = getTo();
      const from = fromRaw ? getMomentDateObj(fromRaw) : null;
      const to = toRaw ? getMomentDateObj(toRaw) : null;

      if ((fromRaw && !from?.isValid()) || (toRaw && !to?.isValid())) {
        return '';
      }
      if (from?.isAfter(today, 'day')) {
        return ASSIGNMENTS_STATUS.FUTURE;
      }
      if (to?.isBefore(today, 'day')) {
        return '';
      }
      return ASSIGNMENTS_STATUS.ACTIVE;
    }

    return '';
  };

  const status = resolveStatus();
  const displayValue = status ? ASSIGNMENTS_STATUS_OPTIONS[status] : '';

  const value =
    status === ASSIGNMENTS_STATUS.PENDING ? (
      <Tooltip title={PENDING_ASSIGNMENT_TOOLTIP} arrow placement="top">
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
          {displayValue}
          <InfoIcon role="presentation" sx={{ fill: 'var(--mui-palette-text-secondary)', fontSize: 18 }} />
        </Box>
      </Tooltip>
    ) : (
      displayValue
    );

  return <TextField value={value} label={getLabel()} {...props} />;
};

export default AssignmentStatusField;
